import { bindings } from '../'
import getActivityIds from '@polleverywhere/core/lib/util/get_activity_ids'
import Job from '@polleverywhere/core/lib/models/job'
import { createSlideSet } from './slideset'
import urlsToModels from './util/urls_to_models'
import hydrateModels from './util/hydrate_models'
import reduceToTopLevelModels from './util/reduce_to_top_level_models'
import { format } from 'date-fns'
import { defaults } from 'underscore'
import URI from 'urijs'

function stripFileExtension (fileName = '') {
  return new URI(fileName).suffix('').filename()
}

/**
 * Constructs a name to be used for the archive when clearing responses.
 *
 * @param {Object} options - Customization options for the archive name.
 * @param {string} options.fileName - The name of the current presentation.
 * @param {Date} options.clickEvent - The date that clearing happened.
 *   Defaults to the current date & time.
 * @param {string} options.username - The PE username of the current user.
 *
 * @return {string} The archive name built from the given parameters.
 */
export function getArchiveName (options = {}) {
  defaults(options, {
    fileName: null,
    date: new Date(),
    username: null
  })

  let name = ''

  if (options.fileName) {
    name += `${stripFileExtension(options.fileName)}-`
  }

  name += format(options.date, 'yyyy-MM-dd hh:mm a')

  if (options.username) {
    name += `-${options.username}`
  }

  return name
}

/**
 * Gets activity models for activities in the deck that can be cleared.
 *
 * @return {Promise<Object[]>} A Promise that is resolved with the models of
 * activities in the deck that can have their responses cleared
 */
export async function activitiesToClear () {
  const urlsInDeck = await bindings.allActivityUrls()
  let models = urlsToModels(urlsInDeck)

  // Join instructions are legit PE slides, but irrelevant for response clearing
  models = models.filter(model => model !== 'join_instructions')

  models = await hydrateModels(models)
  return reduceToTopLevelModels(models)
}

/**
 * Clears responses of the given activities.
 *
 * @param {Object|Object[]} activities Either a single activity model or an array
 * of activity models whose responses to clear. It's expected that these models are
 * hydrated before being passed in.
 * @param {string} archiveName The name to use for the resulting archives.
 * @return {Promise<Object[]>} A Promise that is resolved with the activity models
 * that are cleared.
 */
export async function clearResponses (activities, archiveName = getArchiveName()) {
  if (!activities) {
    throw new Error('No activities given to clear responses')
  }

  activities = [activities].flat()
  if (!activities.length) {
    return []
  }

  const data = getActivityIds(activities)
  data.name = archiveName

  await Job.create('/api/activities/clear', data).promise
  await bindings.updateAllSlides((await createSlideSet(activities)).getData())
  return activities
}
